import { gql } from '@apollo/client';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { fromError } from '~utils/errors';
import { useCreatePowerScheduleMutation } from './__generated__/CreatePowerScheduleModal.graphql';

const FormData = z.object({
  customerId: z.string(),
  title: z.string().min(1),
  description: z.string().optional(),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  customerId: string;
  isOpen: boolean;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
}

export function CreatePowerScheduleModal({ customerId, isOpen, onCancel, onSuccess }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <CreatePowerScheduleModalContent
          customerId={customerId}
          initialFocusRef={initialFocusRef}
          onCancel={onCancel}
          onSuccess={onSuccess}
        />
      </ModalContent>
    </Modal>
  );
}

type ContentProps = {
  customerId: string;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onCancel: () => void;
  onSuccess: (id: string) => Promise<void> | void;
};

function CreatePowerScheduleModalContent({
  customerId,
  initialFocusRef,
  onCancel,
  onSuccess,
}: ContentProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      customerId,
      title: '',
    },
    resolver: zodResolver(FormData),
  });
  const { ref: titleInputRef, ...titleInputProps } = register('title');

  const [createPowerSchedule] = useCreatePowerScheduleMutation();

  const onSubmit = useCallback(
    async ({ customerId, title, description }: FormData) => {
      try {
        const { data } = await createPowerSchedule({
          variables: {
            input: {
              customerId,
              title,
              description,
            },
          },
        });

        if (!data || !data.powerScheduleCreate.powerSchedule) {
          throw new Error('Create power schedule failed');
        }

        await onSuccess?.(data.powerScheduleCreate.powerSchedule.id);
      } catch (err) {
        toast({
          status: 'error',
          title: 'Cannot create power schedule',
          description: fromError(err, 'CreatePowerSchedule', {
            POWER_SCHEDULE_LIMIT_REACHED:
              "Looks like you've reached the total number of power schedules that you can create per customer.",
            POWER_SCHEDULE_EMPTY_TITLE:
              'Looks like you are trying to create a power schedule with an empty title.',
          }),
        });
      }
    },
    [toast, createPowerSchedule, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>New power schedule</ModalHeader>
      <ModalCloseButton onClick={onCancel} />
      <ModalBody>
        <VStack>
          <FormControl isRequired isInvalid={Boolean(errors.title)} marginBottom={5}>
            <FormLabel>Schedule title</FormLabel>
            <Input
              ref={(r) => {
                titleInputRef(r);
                initialFocusRef.current = r;
              }}
              {...titleInputProps}
            />
            <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={Boolean(errors.description)}>
            <FormLabel>Description</FormLabel>
            <Textarea {...register('description')} background="white" borderRadius="base" />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" colorScheme="blue" onClick={onCancel} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
        >
          Create schedule
        </Button>
      </ModalFooter>
    </form>
  );
}

CreatePowerScheduleModal.graphql = {
  mutations: {
    CreatePowerSchedule: gql`
      mutation CreatePowerSchedule($input: PowerScheduleCreateInput!) {
        powerScheduleCreate(input: $input) {
          powerSchedule {
            id
            ...PowerScheduleTable_powerSchedule
          }
        }
      }
    `,
  },
};
